import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../firebase/config';
import { likePost, unlikePost } from '../services/postService';

/**
 * Hook para manejar likes de un post
 */
export const useLike = (postId, userId, initialLikesCount = 0) => {
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(initialLikesCount);
  const [loading, setLoading] = useState(true);

  // Actualizar likesCount cuando cambie el valor inicial
  useEffect(() => {
    setLikesCount(initialLikesCount);
  }, [initialLikesCount]);

  // Verificar si el usuario ya le dio like
  useEffect(() => {
    const checkIfLiked = async () => {
      if (!postId || !userId) {
        setLoading(false);
        return;
      }

      try {
        const likesRef = collection(db, 'likes');
        const q = query(
          likesRef,
          where('postId', '==', postId),
          where('userId', '==', userId),
          limit(1)
        );

        const snapshot = await getDocs(q);
        setLiked(!snapshot.empty);
      } catch (error) {
        console.error('Error al verificar like:', error);
      } finally {
        setLoading(false);
      }
    };

    checkIfLiked();
  }, [postId, userId]);

  // Toggle like - 100% optimista, sin bloqueos
  const toggleLike = async () => {
    if (!userId) return;

    // Capturar estado actual ANTES de cambiar
    const wasLiked = liked;
    const prevCount = likesCount;

    // Actualizar UI INSTANTÁNEAMENTE
    setLiked(!wasLiked);
    setLikesCount(wasLiked ? Math.max(0, likesCount - 1) : likesCount + 1);

    // Backend en segundo plano - NO bloquea la UI
    try {
      const result = wasLiked 
        ? await unlikePost(postId, userId)
        : await likePost(postId, userId);
      
      if (!result.success) {
        // Solo revertir si falla
        setLiked(wasLiked);
        setLikesCount(prevCount);
      }
    } catch (error) {
      // Revertir silenciosamente
      setLiked(wasLiked);
      setLikesCount(prevCount);
    }
  };

  return {
    liked,
    likesCount,
    loading,
    toggleLike
  };
};
